import React, { useMemo, useState } from 'react';
import { Text, View } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '../../../src/theme/useTheme';
import { useThemeContext } from '../../../src/theme/ThemeContext';
import { getSportDefinitionOrDefault } from '../../../src/sports/sports.constants';
import { SoccerKit } from '../../ui/SoccerKit/SoccerKit';
import { DEFAULT_TEAM_KIT_CONFIG, type TeamKitConfig } from '../../../constants/teamKitColors';
import { getTeamCrestStyles } from './TeamCrest.styles';
import { TEAM_CREST_TEST_IDS, buildMonogram } from './TeamCrest.constants';

export interface TeamCrestProps {
  teamName: string;
  /** Sport key from the competition; falls back to football when absent. */
  sport?: string | null;
  /** Kit colours from the DB. Football only; ignored by logo sports. */
  kit?: Partial<TeamKitConfig> | null;
  /** Crest/logo URL. Logo sports only. */
  logoUrl?: string | null;
  size?: number;
}

/**
 * Team identity, drawn the way the sport expects (specs/multi-sport-core).
 *
 * Football teams are drawn as kit SVGs — the app deliberately prefers kits and flags over
 * crests, so `SoccerKit` stays the football path untouched. Sports with no kit vocabulary use
 * a logo, falling back to an initials monogram when none is available.
 *
 * The fallback is never a bare dot: the existing football rule ("always show the SVG kit,
 * never a dot") applies equally here.
 */
export const TeamCrest: React.FC<TeamCrestProps> = ({
  teamName,
  sport,
  kit,
  logoUrl,
  size = 40,
}) => {
  const theme = useTheme();
  const { themeMode } = useThemeContext();
  const styles = useMemo(() => getTeamCrestStyles(theme, themeMode, size), [theme, themeMode, size]);
  const [logoFailed, setLogoFailed] = useState(false);

  const definition = getSportDefinitionOrDefault(sport);

  if (definition.identityMode === 'KIT') {
    return (
      <View style={styles.root} testID={TEAM_CREST_TEST_IDS.kit}>
        <SoccerKit
          primary={kit?.primary ?? DEFAULT_TEAM_KIT_CONFIG.primary}
          secondary={kit?.secondary ?? DEFAULT_TEAM_KIT_CONFIG.secondary}
          type={kit?.type ?? DEFAULT_TEAM_KIT_CONFIG.type}
          size={size}
        />
      </View>
    );
  }

  if (logoUrl && !logoFailed) {
    return (
      <View style={styles.root} testID={TEAM_CREST_TEST_IDS.logo}>
        <Image
          source={{ uri: logoUrl }}
          style={styles.logo}
          contentFit="contain"
          onError={() => setLogoFailed(true)}
          accessibilityLabel={teamName}
        />
      </View>
    );
  }

  return (
    <View style={styles.root} testID={TEAM_CREST_TEST_IDS.monogram}>
      <View style={styles.monogram} accessibilityLabel={teamName}>
        <Text style={styles.monogramText}>{buildMonogram(teamName)}</Text>
      </View>
    </View>
  );
};
